import { waitForItPromise } from 'src/lib/utils';
import { CollideEvent, CollideId, isOneHitCollider } from '../../defs/collide';
import type { FirecrackerBoxBlockEntity } from '../../entities/BlockEntity';
import {
  despawnBlockEntity,
  setBlockEntityViewLayer,
} from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { CollideHandler, ICollision } from './ICollision';

/*
 * Firecracker Box collision resolver
 */
export class FirecrackerBoxCollision implements ICollision {
  private readonly scene: GameScene;
  private readonly entity: FirecrackerBoxBlockEntity;

  private readonly resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this.resolveTap,
    block: this._resolveBlock,
    attack: this.resolveAttack,
  };

  constructor(scene: GameScene, entity: FirecrackerBoxBlockEntity) {
    this.scene = scene;
    this.entity = entity;
  }

  public async resolve(event: CollideEvent): Promise<boolean> {
    return this.resolvers[event.id]?.call(this, event) ?? false;
  }

  private async resolveTap(_: CollideEvent) {
    this.entity.c.blockFirecrackerBox.view.shake();
    return false;
  }

  private async _resolveBlock(event: CollideEvent) {
    const collider = event.collider;

    // if block collider
    if (collider?.type === 'block') {
      const colliderProps = collider.entity.c.block.props;

      // if basic block collider and matches chameleon current color
      if (
        colliderProps.type === 'basic' &&
        this.entity.c.blockFirecrackerBox.activeColor === colliderProps.color
      ) {
        await this.attack();
        return true;
      }
    }
    return false;
  }

  private async resolveAttack(event: CollideEvent) {
    if (isOneHitCollider(event.collider?.type)) {
      this.entity.c.blockFirecrackerBox.activeCount = 0;
    }
    await this.attack();
    return true;
  }

  private async attack() {
    const entity = this.entity;
    const component = entity.c.blockFirecrackerBox;

    // push activations
    const phase = this.scene.sessionEntity.c.phase;
    phase.activePush();

    // Move to the middle layer.
    setBlockEntityViewLayer(this.scene, entity, 'middle');

    // play launch animation.
    await component.launch();

    // Restore the default layer.
    setBlockEntityViewLayer(this.scene, entity, 'default');
    await phase.activePop();

    // Poke if not the last one.
    if (component.activeCount > 0) {
      this.scene?.events.publish({ id: 'poke' });
      return;
    }

    // despawn block
    despawnBlockEntity(this.scene, this.entity);
  }
}
